import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { apiGet } from '../../api/client'
import { Spinner } from '../../ui/Spinner'
import { n, post, useMsg } from './AutonomyPage'

type AuthorityRow = {
  request_type: string
  requester_class: string
  min_trust: number
  decision: string
  confirm_via?: string
  approver?: string
  source: string
  explain?: string
}

type AuthorityPage = {
  ok: boolean
  ready: boolean
  scope: 'company' | 'sp'
  rows: AuthorityRow[]
  decisions: string[]
  confirm_via: string[]
  approvers: string[]
  error?: string
}

const DECISION_COLOR: Record<string, string> = {
  allow: '#2e7d32',
  confirm: '#b54708',
  approve: '#1f5f8b',
  refuse: '#b42318',
}

function authorityQuery(dscope: string) {
  const q = dscope === 'sp' ? 'sp' : ''
  return {
    queryKey: ['settings', 'autonomy', 'authority', q],
    queryFn: () => apiGet<AuthorityPage>(`/settings/autonomy/desk/authority${q ? `?dscope=${q}` : ''}`),
  }
}

export function AuthorityMatrixPanel({
  scope,
  companyName,
  onChange,
}: {
  scope: 'company' | 'sp'
  isGlobal: boolean
  companyName: string
  onChange(): void
}) {
  const dscope = scope === 'sp' ? 'sp' : ''
  const q = useQuery(authorityQuery(dscope))
  const [msg, say] = useMsg()
  const [edit, setEdit] = useState<AuthorityRow | null>(null)

  const rows = useMemo(() => {
    const all = q.data?.rows ?? []
    return all.filter((r) => r.requester_class === '*').slice(0, 40)
  }, [q.data?.rows])

  if (q.isLoading) return <Spinner />
  if (!q.data?.ok) {
    return (
      <div className="panel panel-default" id="odsk-authority">
        <div className="panel-body text-muted" style={{ fontSize: 12 }}>
          Who may ask for what: {q.data?.error ?? 'not available'}.
        </div>
      </div>
    )
  }
  if (!q.data.ready) {
    return (
      <div className="panel panel-default" id="odsk-authority">
        <div className="panel-body text-muted" style={{ fontSize: 12 }}>
          The authority matrix table is not on this database yet — run the Desk Authority migration first.
        </div>
      </div>
    )
  }

  const saveRow = () => {
    if (!edit || !q.data) return
    const payload = {
      scope: q.data.scope,
      request_type: edit.request_type,
      requester_class: edit.requester_class,
      min_trust: String(edit.min_trust),
      decision: edit.decision,
      confirm_via: 'link',
      approver: edit.approver ?? 'oncall',
    }
    const done = () => {
      setEdit(null)
      void q.refetch()
      onChange()
    }
    if (edit.source === 'company' || edit.source === 'provider') {
      post('/desk/authority/delete', { scope: q.data.scope, request_type: edit.request_type, requester_class: edit.requester_class }, say, () => {
        post('/desk/authority', payload, say, done)
      })
    } else {
      post('/desk/authority', payload, say, done)
    }
  }

  const inheritRow = (row: AuthorityRow) => {
    if (row.source === 'default') return
    post(
      '/desk/authority/delete',
      { scope: q.data!.scope, request_type: row.request_type, requester_class: row.requester_class },
      say,
      () => {
        setEdit(null)
        void q.refetch()
        onChange()
      },
      'Removed',
    )
  }

  return (
    <div className="panel panel-default" id="odsk-authority" data-scope={q.data.scope}>
      <div className="panel-body">
        <strong style={{ fontSize: 14 }}>Who may ask for what</strong>
        <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
          Authority matrix (request type × any requester). Provider and company rows override the built-in defaults.
        </span>
        <div className="odx-inherit" style={{ marginTop: 8 }}>
          {scope === 'sp' ? 'Rows here apply to every company without its own.' : `Rows here apply to ${companyName} only.`}
        </div>
        <table className="table table-condensed" style={{ fontSize: 12, marginTop: 10 }}>
          <thead>
            <tr>
              <th>Request type</th>
              <th>Decision</th>
              <th>Trust</th>
              <th>Source</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.request_type}|${row.requester_class}`}>
                <td>
                  <code>{row.request_type}</code>
                  {row.explain && (
                    <div className="text-muted odsk-authority-explain" style={{ fontSize: 11 }}>
                      {row.explain}
                    </div>
                  )}
                </td>
                <td style={{ fontWeight: 600, color: DECISION_COLOR[row.decision] ?? '#444' }}>{row.decision}</td>
                <td>{n(row.min_trust)}</td>
                <td className="text-muted">{row.source}</td>
                <td>
                  <button type="button" className="btn btn-default btn-xs" onClick={() => setEdit({ ...row })}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {edit && (
          <div style={{ marginTop: 12, padding: 10, background: '#f6f7f9', borderRadius: 4, fontSize: 12, maxWidth: 520 }}>
            <strong>
              {edit.request_type} / {edit.requester_class}
            </strong>
            <div style={{ marginTop: 6 }}>
              decision{' '}
              <select className="form-control input-sm" style={{ display: 'inline-block', width: 'auto' }} value={edit.decision} onChange={(e) => setEdit({ ...edit, decision: e.target.value })}>
                {q.data.decisions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: 6 }}>
              min trust{' '}
              <input type="number" min={0} max={3} className="form-control input-sm" style={{ width: 64, display: 'inline-block' }} value={edit.min_trust} onChange={(e) => setEdit({ ...edit, min_trust: parseInt(e.target.value, 10) || 0 })} />
            </div>
            {edit.decision === 'confirm' && (
              <div className="text-muted odsk-authority-confirm-via" style={{ marginTop: 6 }}>
                The requester confirms from a personal link sent to their email. No code is shown on the machine.
              </div>
            )}
            {edit.decision === 'approve' && (
              <div style={{ marginTop: 6 }}>
                approver{' '}
                <select className="form-control input-sm" style={{ display: 'inline-block', width: 'auto' }} value={edit.approver ?? 'oncall'} onChange={(e) => setEdit({ ...edit, approver: e.target.value })}>
                  {q.data.approvers.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {edit.explain && <p className="text-muted" style={{ marginTop: 8, marginBottom: 0 }}>{edit.explain}</p>}
            <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
              <button type="button" className="btn btn-primary btn-sm" onClick={saveRow}>
                Save row
              </button>
              {edit.source !== 'default' && (
                <button type="button" className="btn btn-default btn-sm" onClick={() => inheritRow(edit)}>
                  Inherit default
                </button>
              )}
              <button type="button" className="btn btn-link btn-sm" onClick={() => setEdit(null)}>
                Cancel
              </button>
              {msg}
            </div>
          </div>
        )}
        {!edit && msg}
      </div>
    </div>
  )
}
